export type BlueprintType = 'edition' | 'research';
export type BlueprintTier = 'free' | 'pro' | 'premium';

export interface BlueprintLens {
  purpose?: string;
  priorities?: string[];
  details?: string;
}

export interface Blueprint {
  id: string;
  name: string;
  description: string;
  type: BlueprintType;
  tier: BlueprintTier;
  icon: string;
  color: string;
  category: string;
  tags: string[];
  lens?: BlueprintLens;
  suggestedTopics?: string[];
  promptTemplate?: string;
  createdAt: string;
  featured?: boolean;
}

export const EDITION_BLUEPRINTS: Blueprint[] = [
  {
    id: 'edition-student',
    name: 'Student Edition',
    description: 'Organize courses, research papers, study notes, and academic projects with AI-powered study assistance.',
    type: 'edition',
    tier: 'free',
    icon: 'GraduationCap',
    color: 'bg-blue-500',
    category: 'Education',
    tags: ['academic', 'study', 'research', 'notes'],
    lens: {
      purpose: 'Academic success and learning mastery',
      priorities: ['Course organization', 'Research synthesis', 'Study efficiency'],
      details: 'Track assignments, synthesize lecture notes, and build comprehensive study guides.'
    },
    suggestedTopics: ['Course Notes', 'Research Papers', 'Study Guides', 'Projects'],
    featured: true,
    createdAt: '2024-01-01'
  },
  {
    id: 'edition-kitchen',
    name: 'Kitchen Commander',
    description: 'Master your culinary journey with recipe organization, meal planning, and ingredient management.',
    type: 'edition',
    tier: 'free',
    icon: 'ChefHat',
    color: 'bg-orange-500',
    category: 'Lifestyle',
    tags: ['cooking', 'recipes', 'meal-planning', 'food'],
    lens: {
      purpose: 'Culinary mastery and kitchen efficiency',
      priorities: ['Recipe collection', 'Meal planning', 'Ingredient tracking'],
      details: 'Build your personal cookbook, plan weekly meals, and never forget an ingredient.'
    },
    suggestedTopics: ['Recipes', 'Meal Plans', 'Shopping Lists', 'Techniques'],
    featured: true,
    createdAt: '2024-01-01'
  },
  {
    id: 'edition-business',
    name: 'Business Pro',
    description: 'Strategic business intelligence hub for market research, competitor analysis, and decision making.',
    type: 'edition',
    tier: 'pro',
    icon: 'Briefcase',
    color: 'bg-slate-700',
    category: 'Business',
    tags: ['business', 'strategy', 'analysis', 'planning'],
    lens: {
      purpose: 'Strategic business intelligence and growth',
      priorities: ['Market insights', 'Competitive analysis', 'Strategic planning'],
      details: 'Track industry trends, analyze competitors, and make data-driven decisions.'
    },
    suggestedTopics: ['Market Research', 'Competitors', 'Strategy', 'Metrics'],
    featured: true,
    createdAt: '2024-01-01'
  },
  {
    id: 'edition-creative',
    name: 'Creative Studio',
    description: 'Capture inspiration, develop creative projects, and build your artistic portfolio.',
    type: 'edition',
    tier: 'free',
    icon: 'Palette',
    color: 'bg-purple-500',
    category: 'Creative',
    tags: ['creative', 'art', 'design', 'portfolio'],
    lens: {
      purpose: 'Creative expression and artistic growth',
      priorities: ['Inspiration capture', 'Project development', 'Portfolio building'],
      details: 'Collect references, develop concepts, and showcase your creative journey.'
    },
    suggestedTopics: ['Inspiration', 'Projects', 'References', 'Portfolio'],
    featured: true,
    createdAt: '2024-01-01'
  },
  {
    id: 'edition-health',
    name: 'Health & Wellness',
    description: 'Track your wellness journey with fitness goals, nutrition insights, and mindfulness practices.',
    type: 'edition',
    tier: 'free',
    icon: 'Heart',
    color: 'bg-green-500',
    category: 'Health',
    tags: ['health', 'fitness', 'wellness', 'nutrition'],
    lens: {
      purpose: 'Holistic health and wellness optimization',
      priorities: ['Fitness tracking', 'Nutrition awareness', 'Mental wellness'],
      details: 'Monitor progress, research health topics, and build sustainable habits.'
    },
    suggestedTopics: ['Fitness', 'Nutrition', 'Mindfulness', 'Goals'],
    featured: true,
    createdAt: '2024-01-01'
  },
  {
    id: 'edition-finance',
    name: 'Personal Finance',
    description: 'Master your money with budgeting insights, investment research, and financial planning.',
    type: 'edition',
    tier: 'pro',
    icon: 'Wallet',
    color: 'bg-emerald-600',
    category: 'Finance',
    tags: ['finance', 'budget', 'investing', 'planning'],
    lens: {
      purpose: 'Financial literacy and wealth building',
      priorities: ['Budget management', 'Investment research', 'Goal planning'],
      details: 'Track expenses, research investments, and plan for financial milestones.'
    },
    suggestedTopics: ['Budget', 'Investments', 'Goals', 'Learning'],
    featured: true,
    createdAt: '2024-01-01'
  }
];

export const RESEARCH_BLUEPRINTS: Blueprint[] = [
  {
    id: 'research-literature-review',
    name: 'Literature Review',
    description: 'Systematically analyze and synthesize academic sources on any topic.',
    type: 'research',
    tier: 'free',
    icon: 'BookOpen',
    color: 'bg-indigo-500',
    category: 'Academic',
    tags: ['academic', 'review', 'synthesis', 'sources'],
    promptTemplate: 'Help me conduct a literature review on [TOPIC]. I need to identify key themes, compare methodologies, and synthesize findings across multiple sources.',
    createdAt: '2024-01-01'
  },
  {
    id: 'research-market-analysis',
    name: 'Market Analysis',
    description: 'Deep dive into market trends, competitors, and opportunities.',
    type: 'research',
    tier: 'pro',
    icon: 'TrendingUp',
    color: 'bg-blue-600',
    category: 'Business',
    tags: ['market', 'analysis', 'trends', 'competitors'],
    promptTemplate: 'Analyze the market for [INDUSTRY/PRODUCT]. Include market size, key players, trends, and potential opportunities.',
    createdAt: '2024-01-01'
  },
  {
    id: 'research-topic-explorer',
    name: 'Topic Deep Dive',
    description: 'Comprehensive exploration of any subject from multiple angles.',
    type: 'research',
    tier: 'free',
    icon: 'Compass',
    color: 'bg-teal-500',
    category: 'General',
    tags: ['exploration', 'learning', 'comprehensive'],
    promptTemplate: 'I want to deeply understand [TOPIC]. Break it down into key concepts, history, current developments, and practical applications.',
    createdAt: '2024-01-01'
  },
  {
    id: 'research-comparison',
    name: 'Compare & Contrast',
    description: 'Structured comparison of options, technologies, or approaches.',
    type: 'research',
    tier: 'free',
    icon: 'GitCompare',
    color: 'bg-amber-500',
    category: 'Decision Making',
    tags: ['comparison', 'analysis', 'decision'],
    promptTemplate: 'Compare [OPTION A] vs [OPTION B]. Analyze pros/cons, use cases, costs, and provide a recommendation based on [CRITERIA].',
    createdAt: '2024-01-01'
  },
  {
    id: 'research-problem-solving',
    name: 'Problem Solver',
    description: 'Structured approach to analyzing and solving complex problems.',
    type: 'research',
    tier: 'free',
    icon: 'Lightbulb',
    color: 'bg-yellow-500',
    category: 'Problem Solving',
    tags: ['problem', 'solution', 'analysis', 'strategy'],
    promptTemplate: 'Help me solve this problem: [PROBLEM]. Break down root causes, explore potential solutions, and recommend an action plan.',
    createdAt: '2024-01-01'
  },
  {
    id: 'research-swot',
    name: 'SWOT Analysis',
    description: 'Strategic analysis of strengths, weaknesses, opportunities, and threats.',
    type: 'research',
    tier: 'pro',
    icon: 'Target',
    color: 'bg-red-500',
    category: 'Strategy',
    tags: ['swot', 'strategy', 'planning', 'analysis'],
    promptTemplate: 'Conduct a SWOT analysis for [SUBJECT]. Identify internal strengths/weaknesses and external opportunities/threats with actionable insights.',
    createdAt: '2024-01-01'
  },
  {
    id: 'research-content-brief',
    name: 'Content Brief',
    description: 'Create comprehensive content briefs for articles, videos, or presentations.',
    type: 'research',
    tier: 'free',
    icon: 'FileText',
    color: 'bg-pink-500',
    category: 'Content',
    tags: ['content', 'writing', 'brief', 'planning'],
    promptTemplate: 'Create a content brief for [CONTENT TYPE] about [TOPIC]. Include outline, key points, target audience, and SEO considerations.',
    createdAt: '2024-01-01'
  },
  {
    id: 'research-interview-prep',
    name: 'Interview Prep',
    description: 'Prepare thoroughly for job interviews or informational conversations.',
    type: 'research',
    tier: 'free',
    icon: 'Users',
    color: 'bg-cyan-500',
    category: 'Career',
    tags: ['interview', 'career', 'preparation'],
    promptTemplate: 'Help me prepare for an interview at [COMPANY] for [ROLE]. Research the company, anticipate questions, and help me craft compelling answers.',
    createdAt: '2024-01-01'
  }
];

export const ALL_BLUEPRINTS: Blueprint[] = [...EDITION_BLUEPRINTS, ...RESEARCH_BLUEPRINTS];

export const BLUEPRINT_CATEGORIES = [
  'All',
  'Education',
  'Business',
  'Creative',
  'Health',
  'Finance',
  'Lifestyle',
  'Academic',
  'General',
  'Decision Making',
  'Problem Solving',
  'Strategy',
  'Content',
  'Career'
];
